/**
 * DXF Nester theme — centralised tokens for the redesign.
 *
 * All components import from here so a single toggle flips the entire page.
 * Dark mode is opt-in and persisted in localStorage under `dxfNesterTheme`.
 */

export const LIGHT = {
  bg: '#f8fafc',
  panel: '#ffffff',
  panelAlt: '#f1f5f9',
  border: '#e5e7eb',
  borderStrong: '#cbd5e1',
  text: '#0f172a',
  textMuted: '#64748b',
  textFaint: '#94a3b8',
  accent: '#2563eb',
  accentSoft: '#eff6ff',
  accentBorder: '#93c5fd',
  success: '#059669',
  successSoft: '#d1fae5',
  warn: '#d97706',
  warnSoft: '#fef3c7',
  danger: '#dc2626',
  dangerSoft: '#fef2f2',
  focus: '#3b82f6',
  shadow: '0 1px 2px rgba(15,23,42,0.06), 0 1px 3px rgba(15,23,42,0.04)',
  shadowLg: '0 4px 6px rgba(15,23,42,0.05), 0 10px 20px rgba(15,23,42,0.08)',
}

export const DARK = {
  bg: '#0b1220',
  panel: '#111a2e',
  panelAlt: '#1b2742',
  border: '#1e2b45',
  borderStrong: '#2b3c5e',
  text: '#e2e8f0',
  textMuted: '#94a3b8',
  textFaint: '#64748b',
  accent: '#60a5fa',
  accentSoft: '#1e3a8a33',
  accentBorder: '#1e40af',
  success: '#34d399',
  successSoft: '#064e3b66',
  warn: '#fbbf24',
  warnSoft: '#78350f66',
  danger: '#f87171',
  dangerSoft: '#7f1d1d66',
  focus: '#60a5fa',
  shadow: '0 1px 2px rgba(0,0,0,0.35), 0 1px 3px rgba(0,0,0,0.25)',
  shadowLg: '0 10px 30px rgba(0,0,0,0.45)',
}

/** Per-group distinct colour ring used everywhere material groups appear. */
export const GROUP_COLORS = [
  '#2563eb', '#7c3aed', '#059669', '#dc2626', '#d97706', '#0891b2',
  '#e11d48', '#16a34a', '#0284c7', '#9333ea', '#ca8a04', '#be123c',
]

export function groupColor(idx) {
  if (idx == null || idx < 0) return '#6b7280'
  return GROUP_COLORS[idx % GROUP_COLORS.length]
}

export function getStoredTheme() {
  if (typeof window === 'undefined') return 'light'
  try {
    const v = window.localStorage.getItem('dxfNesterTheme')
    return v === 'dark' ? 'dark' : 'light'
  } catch { return 'light' }
}

export function setStoredTheme(mode) {
  if (typeof window === 'undefined') return
  try { window.localStorage.setItem('dxfNesterTheme', mode) } catch { /* noop */ }
}

export function palette(mode) {
  return mode === 'dark' ? DARK : LIGHT
}

export const STATUS_PILL = {
  draft:  { bg: '#f1f5f9', fg: '#475569', label: 'Draft' },
  quoted: { bg: '#dbeafe', fg: '#1d4ed8', label: 'Quoted' },
  won:    { bg: '#dcfce7', fg: '#166534', label: 'Won' },
  lost:   { bg: '#fee2e2', fg: '#991b1b', label: 'Lost' },
}

export const STATUS_PILL_DARK = {
  draft:  { bg: '#1e293b',  fg: '#94a3b8', label: 'Draft' },
  quoted: { bg: '#1e3a8a55', fg: '#93c5fd', label: 'Quoted' },
  won:    { bg: '#064e3b66', fg: '#6ee7b7', label: 'Won' },
  lost:   { bg: '#7f1d1d66', fg: '#fca5a5', label: 'Lost' },
}

export function statusPill(status, mode) {
  const map = mode === 'dark' ? STATUS_PILL_DARK : STATUS_PILL
  return map[status] || map.draft
}
